import React from 'react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className = '', variant = 'primary', size = 'md', asChild = false, children, ...props },
    ref,
  ) => {
    const baseStyles =
      'aspectly:inline-flex aspectly:items-center aspectly:justify-center aspectly:gap-2 aspectly:font-bold aspectly:cursor-pointer aspectly:transition-all aspectly:active:scale-95 aspectly:disabled:opacity-50 aspectly:disabled:pointer-events-none';

    const variants = {
      primary: 'aspectly:bg-primary aspectly:text-on-primary aspectly:shadow-lg aspectly:shadow-primary/10 aspectly:hover:bg-primary-dim',
      outline: 'aspectly:border aspectly:border-outline-variant/30 aspectly:text-on-surface-variant aspectly:hover:bg-primary/5 aspectly:hover:text-primary',
      ghost: 'aspectly:text-on-surface-variant aspectly:hover:bg-surface-container-low aspectly:hover:text-on-surface',
    };

    const sizes = {
      sm: 'aspectly:px-4 aspectly:py-1.5 aspectly:text-xs aspectly:rounded-full aspectly:uppercase aspectly:tracking-wider',
      md: 'aspectly:px-6 aspectly:py-2.5 aspectly:text-sm aspectly:rounded-xl',
      lg: 'aspectly:px-6 aspectly:py-3 aspectly:text-base aspectly:rounded-xl',
    };

    const combinedClasses = `${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`;

    if (asChild) {
      // Very basic generic unwrapper if asChild is passed (expects a single valid element child like <Link> or <label>)
      if (React.isValidElement(children)) {
        const childProps = children.props as { className?: string };
        return React.cloneElement(children as React.ReactElement<{ className?: string }>, {
          className: `${combinedClasses} ${childProps.className || ''}`.trim(),
          ...props,
        });
      }
    }

    return (
      <button ref={ref} className={combinedClasses} {...props}>
        {children}
      </button>
    );
  },
);
Button.displayName = 'Button';
